import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LoaderIcon } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { userCreditQueries } from "@/api/user-credits";

const TRANSACTION_TYPES = [
  { label: "All", value: "all" },
  { label: "Purchase", value: "purchase" },
  { label: "Usage", value: "usage" },
  { label: "System Grant", value: "system_grant" },
  { label: "Refund", value: "refund" },
];

export function TransactionsTab({ username }: { username: string }) {
  const [transactionType, setTransactionType] = useState("all");
  const [transactionPage, setTransactionPage] = useState(1);

  const { data: transactions, isLoading: isTransactionsLoading } = useQuery(
    userCreditQueries.transactions(username, transactionPage, 10),
  );

  return (
    <>
      {/* Transaction History */}
      <Card className="p-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-semibold">Transaction History</h3>
            <p className="text-sm text-muted-foreground">
              Detailed record of all credit transactions
            </p>
          </div>
          <Select value={transactionType} onValueChange={setTransactionType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              {TRANSACTION_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="relative min-h-[400px]">
          {isTransactionsLoading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <LoaderIcon className="h-8 w-8 animate-spin" />
            </div>
          ) : transactions?.transactions &&
            transactions.transactions.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Credits</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.transactions.map((tx) => (
                    <TableRow key={tx.transaction_id}>
                      <TableCell>
                        {tx.timestamp &&
                          format(new Date(tx.timestamp), "MMM dd, yyyy HH:mm")}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{tx.transaction_type}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        <span
                          className={
                            tx.transaction_type === "usage"
                              ? "text-red-500"
                              : "text-green-500"
                          }
                        >
                          {tx.transaction_type === "usage" ? "-" : "+"}
                          {(tx.amount ?? 0).toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell>{tx.metadata?.type || "-"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {tx.metadata?.description || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Showing {transactions.transactions.length} transactions
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() =>
                      setTransactionPage((page) => Math.max(1, page - 1))
                    }
                    disabled={transactionPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {transactionPage}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setTransactionPage((page) => page + 1)}
                    disabled={!transactions?.transactions.length}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              No transactions found
            </div>
          )}
        </div>
      </Card>
    </>
  );
}
